'use client';

import { useState, useEffect } from 'react';
import {
	ArrowLeft,
	ArrowRight,
	Edit,
	LoaderIcon,
	Crown,
	Zap,
	TrendingUp,
	BarChart3,
	Sparkles
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from './radio-group';
import { Label } from '@/components/ui/label';
import { LeadQuotaBar } from '@/components/ui/lead-quota-bar';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
	'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground hover:bg-primary/90',
				destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
				outline:
					'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
				secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline'
			},
			size: {
				default: 'h-10 px-4 py-2',
				sm: 'h-9 rounded-md px-3',
				lg: 'h-11 rounded-md px-8',
				icon: 'h-10 w-10'
			}
		},
		defaultVariants: {
			variant: 'default',
			size: 'default'
		}
	}
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	}
);
Button.displayName = 'Button';

export enum OutreachStep {
	ProductInfo = 1,
	Goal = 2,
	Summary = 3
}

export function OutreachForm({
	url,
	setUrl,
	productInfo,
	setProductInfo,
	goal,
	setGoal,
	step,
	setStep,
	productInfoFormType,
	setProductInfoFormType,
	outreachFormLoading,
	handleStepChangeAction,
	isGeneratingMoreEmails,
	emailCreationStatus,
	siteId,
	handleGenerateMoreEmails,
	productInfoPlaceholder = 'Enter a description of your product or service'
}: {
	url: string;
	setUrl: (value: string) => void;
	productInfo: string;
	setProductInfo: (value: string) => void;
	goal: string;
	setGoal: (value: string) => void;
	step: OutreachStep;
	setStep: (value: OutreachStep) => void;
	productInfoFormType: 'website' | 'description';
	setProductInfoFormType: (value: 'website' | 'description') => void;
	outreachFormLoading: boolean;
	handleStepChangeAction: (step: OutreachStep, options?: StepChangeOptions) => void;
	isGeneratingMoreEmails: boolean;
	emailCreationStatus: string;
	siteId: string | null;
	handleGenerateMoreEmails: () => Promise<void>;
	productInfoPlaceholder?: string;
}) {
	const productInfoInputRef = React.useRef<HTMLInputElement>(null);
	const goalInputRef = React.useRef<HTMLInputElement>(null);
	const [queingEmails, setQueingEmails] = useState(false);
	const [isEditingProductInfo, setIsEditingProductInfo] = useState(false);
	const [isEditingGoal, setIsEditingGoal] = useState(false);
	const [isSavingInformation, setIsSavingInformation] = useState(false);
	const [newProductInfo, setNewProductInfo] = useState(productInfo);
	const [newGoal, setNewGoal] = useState(goal);
	const [leadQuotaRefresh, setLeadQuotaRefresh] = useState<(() => Promise<void>) | null>(null);
	const [prevIsGeneratingMoreEmails, setPrevIsGeneratingMoreEmails] =
		useState(isGeneratingMoreEmails);

	// For initializing
	useEffect(() => {
		setNewProductInfo(productInfo);
		setNewGoal(goal);
	}, [productInfo, goal]);

	// Track when email generation is complete to refresh quota
	useEffect(() => {
		// If we went from generating to not generating, refresh the quota
		if (
			prevIsGeneratingMoreEmails &&
			!isGeneratingMoreEmails &&
			leadQuotaRefresh &&
			typeof leadQuotaRefresh === 'function'
		) {
			setTimeout(() => {
				leadQuotaRefresh();
			}, 500); // Small delay to ensure backend has processed the new leads
		}
		setPrevIsGeneratingMoreEmails(isGeneratingMoreEmails);
	}, [isGeneratingMoreEmails, prevIsGeneratingMoreEmails, leadQuotaRefresh]);

	const handleNextStep = () => {
		handleStepChangeAction(step);
	};

	const handlePrevStep = () => {
		if (step === OutreachStep.Summary) {
			setStep(OutreachStep.Goal);
			setTimeout(() => {
				goalInputRef.current?.focus();
			}, 50);
		} else if (step === OutreachStep.Goal) {
			setStep(OutreachStep.ProductInfo);
			setTimeout(() => {
				productInfoInputRef.current?.focus();
			}, 50);
		} else if (step === OutreachStep.ProductInfo) {
			return;
		}
	};

	const handleSavingEditInformation = async function () {
		if (goal === newGoal) {
			setIsEditingGoal(false);
		}
		if (productInfo === newProductInfo) {
			setIsEditingProductInfo(false);
		}
		if (goal === newGoal && productInfo === newProductInfo) {
			return;
		}
		setIsSavingInformation(true);
		try {
			const editedStrategy = await fetch(`/api/sites/${siteId}/ai-strategies`, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					prompt: newProductInfo,
					whyPrompt: newGoal
				})
			});
			if (!editedStrategy.ok) {
				throw new Error('Failed to save information');
			}
			setIsEditingGoal(false);
			setIsEditingProductInfo(false);
			setGoal(newGoal);
			setProductInfo(newProductInfo);
		} catch (err: any) {}
		setIsSavingInformation(false);
	};

	return (
		<div className="container mx-auto px-4">
			<div className="max-w-3xl mx-auto">
				<div className="flex items-center mb-6">
					{step > 1 && step < 3 && (
						<Button
							variant="ghost"
							className="text-gray-900"
							size="sm"
							onClick={handlePrevStep}
						>
							<ArrowLeft className="h-4 w-4 mr-2" />
							Back
						</Button>
					)}
					{step < 3 && <span className="text-gray-900 ml-4">Step {step} of 3</span>}
				</div>

				<div className="bg-white/90 backdrop-blur-lg rounded-lg p-6 space-y-6 border border-gray-200">
					{step === OutreachStep.ProductInfo && (
						<div className="space-y-4">
							<h2 className="text-2xl font-bold text-gray-900">
								What Products or Services do you want to outreach about?
							</h2>
							<RadioGroup
								// defaultValue={productInfoFormType}
								className="flex space-x-4"
								value={productInfoFormType}
								disabled={outreachFormLoading}
								onValueChange={(value) => {
									console.log(value);
									setProductInfoFormType(value as 'website' | 'description');
								}}
							>
								<div className="flex items-center space-x-2">
									<RadioGroupItem value="website" id="website" />
									<Label htmlFor="website" className="text-gray-900">
										Website
									</Label>
								</div>
								<div className="flex items-center space-x-2">
									<RadioGroupItem value="description" id="description" />
									<Label htmlFor="description" className="text-gray-900">
										Description
									</Label>
								</div>
							</RadioGroup>
							{productInfoFormType === 'website' ? (
								<Input
									name="productInfo"
									ref={productInfoInputRef}
									placeholder="Enter your website URL"
									value={url}
									onFocus={(e) => {
										if (!e.target.value) {
											setUrl('https://');
										}
									}}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											handleNextStep();
										}
									}}
									disabled={outreachFormLoading}
									type="url"
									onChange={(e) => setUrl(e.target.value)}
									className="bg-white/90 text-gray-900 placeholder:text-gray-500"
								/>
							) : (
								<Textarea
									name="productInfo"
									// onKeyDown={(e) => {
									// 	if (e.key === 'Enter') {
									// 		handleNextStep();
									// 	}
									// }}
									disabled={outreachFormLoading}
									placeholder={productInfoPlaceholder}
									value={productInfo}
									onChange={(e) => setProductInfo(e.target.value)}
									className="bg-white/90 text-gray-900 placeholder:text-gray-500"
								/>
							)}
							<Button
								className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
								onClick={handleNextStep}
								disabled={outreachFormLoading}
							>
								{outreachFormLoading ? 'Loading...' : 'Next'}
								{outreachFormLoading ? (
									<LoaderIcon className="h-4 w-4 ml-2 animate-spin" />
								) : (
									<ArrowRight className="h-4 w-4 ml-2" />
								)}
							</Button>
						</div>
					)}

					{step === OutreachStep.Goal && (
						<div className="space-y-4">
							<h2 className="text-2xl font-bold text-gray-900">
								Who do you want to target?
							</h2>
							<p className="text-lg text-gray-700">
								Remove or edit targeting that's not relevant.
							</p>
							<Button
								className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
								onClick={handleNextStep}
								disabled={outreachFormLoading}
							>
								{outreachFormLoading ? 'Loading...' : 'Find and Generate Emails'}
								{outreachFormLoading ? (
									<LoaderIcon className="h-4 w-4 ml-2 animate-spin" />
								) : (
									<ArrowRight className="h-4 w-4 ml-2" />
								)}
							</Button>
						</div>
					)}

					{step === OutreachStep.Summary && (
						<div className="space-y-6">
							{/* Product/Service and Goal - Side by Side */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{/* Product/Service Info */}
								<div className="bg-white/90 p-4 rounded-lg relative border border-gray-200">
									<h3 className="text-gray-900 font-semibold mb-2">
										Product Or Service
									</h3>
									{isEditingProductInfo ? (
										<div>
											<Textarea
												value={newProductInfo}
												onChange={(e) => setNewProductInfo(e.target.value)}
												className="bg-white text-gray-900 placeholder:text-gray-500"
											/>
											<Button
												variant="default"
												onClick={() => handleSavingEditInformation()}
												disabled={isSavingInformation}
												className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
											>
												{isSavingInformation ? 'Saving...' : 'Save'}
											</Button>
										</div>
									) : (
										<>
											<p
												className="text-gray-700 line-clamp-2 cursor-pointer"
												onClick={() => setIsEditingProductInfo(true)}
											>
												{newProductInfo}
											</p>
											<Button
												variant="ghost"
												size="icon"
												className="absolute top-2 right-2 text-gray-600 hover:text-gray-900"
												onClick={() => setIsEditingProductInfo(true)}
											>
												<Edit className="h-4 w-4" />
												<span className="sr-only">
													Edit Product/Service Information
												</span>
											</Button>
										</>
									)}
								</div>

								{/* Outreach Goal */}
								<div className="bg-white/90 p-4 rounded-lg relative border border-gray-200">
									<h3 className="text-gray-900 font-semibold mb-2">
										Outreach Goal
									</h3>
									{isEditingGoal ? (
										<div>
											<Textarea
												value={newGoal}
												onChange={(e) => setNewGoal(e.target.value)}
												className="bg-white text-gray-900 placeholder:text-gray-500"
											/>
											<Button
												variant="default"
												onClick={() => handleSavingEditInformation()}
												disabled={isSavingInformation}
												className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
											>
												{isSavingInformation ? 'Saving...' : 'Save'}
											</Button>
										</div>
									) : (
										<>
											<p
												className="text-gray-700 line-clamp-2 cursor-pointer"
												onClick={() => setIsEditingGoal(true)}
											>
												{newGoal}
											</p>
											<Button
												variant="ghost"
												size="icon"
												className="absolute top-2 right-2 text-gray-600 hover:text-gray-900"
												onClick={() => setIsEditingGoal(true)}
											>
												<Edit className="h-4 w-4" />
												<span className="sr-only">Edit Outreach Goal</span>
											</Button>
										</>
									)}
								</div>
							</div>

							{/* Lead Quota Bar - Full Width */}
							<LeadQuotaBar siteId={siteId} onRefreshData={setLeadQuotaRefresh} />

							{/* Generate More Emails Button */}
							{isGeneratingMoreEmails ? (
								<>
									<Button
										variant="default"
										className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
										disabled
									>
										Creating Emails... <LoaderIcon className="animate-spin" />
									</Button>
									<p className="text-sm italic font-extralight text-gray-600 animate-pulse">
										... {emailCreationStatus || 'Loading'} ...
									</p>
								</>
							) : (
								<>
									<Button
										variant="default"
										onClick={handleGenerateMoreEmails}
										className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
									>
										Generate More Emails
									</Button>
								</>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
